import { useCallback, useEffect, useMemo, useRef } from "react"

import { useRunOutliersTransport } from "@/runs"
import { toast } from "@coval/ui"

import type { RunRowLike } from "@/types/RunRow"

import type { RunResultsMetricRenderAdaptersById } from "../types"
import type { OutlierEvidenceBySimulationId } from "../utils/buildOutlierEvidence"
import { promoteOutlierRows } from "../utils/promoteOutlierRows"
import { shapeSelectedOutliers } from "../utils/shapeSelectedOutliers"

const EMPTY_OUTLIER_IDS: ReadonlySet<string> = new Set()
const EMPTY_OUTLIER_EVIDENCE: OutlierEvidenceBySimulationId = new Map()
const EMPTY_METRIC_ID_ALIASES: Readonly<Record<string, string>> = {}

interface UseRunResultsOutlierRowsParams {
  metricIdAliases?: Readonly<Record<string, string>>
  filteredRunRows: RunRowLike[]
  runRows?: RunRowLike[]
  sourceRunRows?: RunRowLike[]
  isOutliersEnabled: boolean
  isShared: boolean
  metricIdFilter: string | null
  metricRenderAdaptersById: RunResultsMetricRenderAdaptersById
  outlierMetricIds: string[] | null
  reorderRows: (rows: RunRowLike[]) => RunRowLike[]
  runId?: string
}

function buildRowDisplayIndex(rows: RunRowLike[]): Map<RunRowLike, number> {
  return new Map(rows.map((row, index) => [row, index]))
}

export function useRunResultsOutlierRows({
  metricIdAliases = EMPTY_METRIC_ID_ALIASES,
  filteredRunRows,
  runRows = filteredRunRows,
  sourceRunRows,
  isOutliersEnabled,
  isShared,
  metricIdFilter,
  metricRenderAdaptersById,
  outlierMetricIds,
  reorderRows,
  runId,
}: UseRunResultsOutlierRowsParams) {
  const outliersEnabled = !isShared && Boolean(runId) && isOutliersEnabled
  const outliersQuery = useRunOutliersTransport(runId, {
    enabled: outliersEnabled,
  })
  const selectedOutliers = useMemo(
    () =>
      shapeSelectedOutliers({
        metricIdAliases,
        runRows,
        sourceRunRows,
        metricRenderAdaptersById,
        metricSamples: outliersQuery.data?.metricSamples ?? [],
        outliers: outliersQuery.data?.outliers ?? [],
        selectedMetricIds: outlierMetricIds,
      }),
    [
      metricIdAliases,
      runRows,
      sourceRunRows,
      metricRenderAdaptersById,
      outliersQuery.data?.metricSamples,
      outliersQuery.data?.outliers,
      outlierMetricIds,
    ],
  )
  const outlierSimulationOutputIds = outliersEnabled
    ? selectedOutliers.qualifyingSimulationOutputIds
    : EMPTY_OUTLIER_IDS
  const outlierEvidenceBySimulationId = outliersEnabled
    ? selectedOutliers.outlierEvidenceBySimulationId
    : EMPTY_OUTLIER_EVIDENCE
  const isOutlierAnalysisPending =
    outliersEnabled &&
    !outliersQuery.isError &&
    outliersQuery.data?.isComplete !== true

  const hasShownOutliersUnavailable = useRef(false)
  useEffect(() => {
    if (!outliersQuery.isError) {
      hasShownOutliersUnavailable.current = false
      return
    }
    if (!hasShownOutliersUnavailable.current) {
      hasShownOutliersUnavailable.current = true
      toast.danger({
        title: "Outliers unavailable",
        description: "Could not load outliers for this run.",
        duration: 6000,
      })
    }
  }, [outliersQuery.isError])

  const { scopedRunRows, scopedRowDisplayIndex } = useMemo(() => {
    const metricRecord = metricIdFilter
      ? metricRenderAdaptersById[metricIdFilter]
      : undefined
    const filteredRows = metricRecord?.id
      ? filteredRunRows.filter(
          (row) => row.metric_outputs?.[metricRecord.id] != null,
        )
      : filteredRunRows
    const reorderedRows = metricRecord?.id
      ? reorderRows(filteredRows)
      : filteredRows

    return {
      scopedRunRows: outliersEnabled
        ? promoteOutlierRows(reorderedRows, outlierSimulationOutputIds)
        : reorderedRows,
      scopedRowDisplayIndex: buildRowDisplayIndex(filteredRows),
    }
  }, [
    filteredRunRows,
    metricIdFilter,
    metricRenderAdaptersById,
    outlierSimulationOutputIds,
    outliersEnabled,
    reorderRows,
  ])

  const getOutlierCellVariant = useCallback(
    (row: RunRowLike, columnId: string) => {
      if (
        !outliersEnabled ||
        row.simulation_output_id == null ||
        !outlierSimulationOutputIds.has(row.simulation_output_id)
      ) {
        return undefined
      }
      if (columnId === "simulationOutputId") {
        return "warning" as const
      }
      return selectedOutliers.qualifyingMetricIdsBySimulationId
        .get(row.simulation_output_id)
        ?.has(metricRenderAdaptersById[columnId]?.id ?? columnId)
        ? ("warning" as const)
        : undefined
    },
    [
      metricRenderAdaptersById,
      outlierSimulationOutputIds,
      outliersEnabled,
      selectedOutliers.qualifyingMetricIdsBySimulationId,
    ],
  )

  return {
    getOutlierCellVariant,
    isOutlierAnalysisPending,
    outlierEvidenceBySimulationId,
    outlierSimulationOutputIds,
    outliersEnabled,
    outliersQuery,
    scopedRowDisplayIndex,
    scopedRunRows,
    selectedOutliers,
  }
}
